import { isBooleanFieldChecked } from "utils/helpers/booleanField";
import { evaluateConditionNode, isFieldVisible } from "./conditions";
import {
  FIELD_INPUT_TYPES,
  VALIDATION_MESSAGE_KEYS,
  VALIDATORS,
} from "./constants";
import { isBlank, isNil } from "./utils";
import { resolveMessage, validators } from "./validators";

const TYPE_FALLBACK_RULES = {
  [FIELD_INPUT_TYPES.EMAIL]: [
    {
      validator: VALIDATORS.EMAIL,
      messageKey: VALIDATION_MESSAGE_KEYS.INVALID_EMAIL,
    },
  ],
  [FIELD_INPUT_TYPES.PHONE]: [
    {
      validator: VALIDATORS.PHONE,
      messageKey: VALIDATION_MESSAGE_KEYS.INVALID_PHONE,
    },
  ],
  [FIELD_INPUT_TYPES.NUMBER]: [],
  [FIELD_INPUT_TYPES.DATE]: [],
  [FIELD_INPUT_TYPES.TEXT]: [],
};

export const buildFieldRules = (field) => {
  const rules = [];
  if (field && field.required) {
    rules.push({
      validator: VALIDATORS.REQUIRED,
      messageKey: VALIDATION_MESSAGE_KEYS.REQUIRED,
    });
  }
  const custom = (field && field.rules) || [];
  for (const rule of custom) {
    if (rule && rule.validator) rules.push(rule);
  }
  if (!custom.length && field && field.type) {
    for (const rule of TYPE_FALLBACK_RULES[field.type] || []) {
      rules.push(rule);
    }
  }
  return rules;
};

export const evaluateRule = (
  rule,
  value,
  context = {},
  messages = {},
  now = new Date(),
) => {
  if (!rule || !rule.validator) return "";
  if (rule.when && !evaluateConditionNode(rule.when, context, now)) return "";
  const validator = validators[rule.validator];
  if (!validator) return "";
  if (rule.validator !== VALIDATORS.REQUIRED && isBlank(value)) return "";
  const result = validator(value, rule, context, now);
  if (result.valid) return "";
  return resolveMessage(result, rule, messages);
};

const matchesRequiredValue = (value, field) => {
  const requiredValue = field.requiredValue;
  if (requiredValue === true || requiredValue === "yes") {
    return isBooleanFieldChecked(value);
  }
  return String(value) === String(requiredValue);
};

export const evaluateField = (
  field,
  value,
  context = {},
  messages = {},
  now = new Date(),
) => {
  if (!field) return "";
  if (!isFieldVisible(field, context, now)) return "";
  const rules = buildFieldRules(field);
  for (const rule of rules) {
    const error = evaluateRule(rule, value, context, messages, now);
    if (error) return error;
  }
  if (
    (field.type === FIELD_INPUT_TYPES.NUMBER ||
      field.type === FIELD_INPUT_TYPES.SLIDER) &&
    !isBlank(value) &&
    Number.isNaN(Number(value))
  ) {
    return (
      messages[VALIDATION_MESSAGE_KEYS.INVALID_NUMBER] ||
      VALIDATION_MESSAGE_KEYS.INVALID_NUMBER
    );
  }
  if (field.type === FIELD_INPUT_TYPES.DATE && !isBlank(value)) {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return (
        messages[VALIDATION_MESSAGE_KEYS.INVALID_DATE] ||
        VALIDATION_MESSAGE_KEYS.INVALID_DATE
      );
    }
  }
  if (!isNil(field.requiredValue) && !matchesRequiredValue(value, field)) {
    return (
      messages[VALIDATION_MESSAGE_KEYS.REQUIRED] ||
      VALIDATION_MESSAGE_KEYS.REQUIRED
    );
  }
  return "";
};

export const readItemField = (item, fieldName) => {
  if (!item || !fieldName) return undefined;
  if (Object.prototype.hasOwnProperty.call(item, fieldName)) {
    return item[fieldName];
  }
  return fieldName.split(".").reduce((current, key) => {
    if (current == null) return undefined;
    return current[key];
  }, item);
};

const collectionItemsForAccessor = (accessor, context) => {
  const dot = accessor.indexOf(".");
  if (dot <= 0) return null;
  const entity = accessor.slice(0, dot);
  const fieldName = accessor.slice(dot + 1);
  if (!fieldName) return null;
  const items = context && context.fields && context.fields[entity];
  if (!Array.isArray(items)) return null;
  return { fieldName, items };
};

export const evaluateFormDefinition = (
  formDefinition,
  valuesByAccessor,
  context = {},
  messages = {},
  now = new Date(),
  { readValue, isRepeatingAccessor } = {},
) => {
  const fields = (formDefinition && formDefinition.fields) || [];
  const fieldErrors = {};
  for (const field of fields) {
    if (!field || field.display) continue;
    if (!isFieldVisible(field, context, now)) continue;
    const accessor =
      field.accessor ||
      (field.key
        ? String(field.key).indexOf(".") >= 0
          ? field.key
          : `fields.${field.key}`
        : null);
    if (!accessor) continue;
    const collection = collectionItemsForAccessor(accessor, context);
    if (
      collection ||
      (typeof isRepeatingAccessor === "function" &&
        isRepeatingAccessor(accessor, field))
    ) {
      const items = collection ? collection.items : [];
      const fieldName = collection
        ? collection.fieldName
        : accessor.slice(accessor.indexOf(".") + 1);
      items.forEach((item, index) => {
        const value = readItemField(item, fieldName);
        const error = evaluateField(field, value, context, messages, now);
        if (error) fieldErrors[`${accessor}[${index}]`] = error;
      });
      continue;
    }
    const value =
      typeof readValue === "function"
        ? readValue(accessor, field)
        : valuesByAccessor[accessor];
    const error = evaluateField(field, value, context, messages, now);
    if (error) fieldErrors[accessor] = error;
  }
  return fieldErrors;
};
